/* globals google */
import { ILocation } from '@rbi-ctg/frontend';

import noop from '../noop';

import { GOOGLE_MAPS_KEY } from './api-keys';
import { GET_CURRENT_POSITION_TIMEOUT, GOOGLE_MAPS_API_BASE_URL } from './constants';
import { GeolocationPermissionStates, PermissionState } from './types';

export { parsePlaceAddress } from './parse-place-address';
export * from './types';
export const GOOGLE_API_SRC_PATH = `${GOOGLE_MAPS_API_BASE_URL}/js?key=${GOOGLE_MAPS_KEY}&libraries=places`;

declare global {
  interface Window {
    google: typeof google;
  }
}

let scriptLoadingListeners: Array<(value?: unknown) => void> = [];
let scriptLoadingListenersReject: Array<(value?: unknown) => void> = [];

export function geolocationLibraryIsLoaded() {
  const loadedLibraryScriptElement = document.querySelector('script#google.loaded');
  return !!loadedLibraryScriptElement;
}

function clearListenerArrays() {
  scriptLoadingListeners = [];
  scriptLoadingListenersReject = [];
}

export function loadGeolocationLibrary(): Promise<void> {
  return new Promise((resolve, reject) => {
    // only load the library once
    // if it is already loaded, we can resolve immediately
    if (geolocationLibraryIsLoaded()) {
      resolve();
    }

    // if library is not yet loaded, push promise
    //  resolve / reject fns to arrays so we can
    //  notifiy caller when it is ready
    scriptLoadingListeners.push(resolve);
    scriptLoadingListenersReject.push(reject);

    // if this is the first request for the library
    // we won't have a script for it in the head yet
    if (!document.querySelector('script#google')) {
      // kick off async loading of library
      const script = document.createElement('script');
      script.src = GOOGLE_API_SRC_PATH;
      script.id = 'google';
      script.async = true;
      // add script tag to document head
      document.head.appendChild(script);
      // create listeners to notify callers when
      //  it is ready (or if it failed to load)
      script.addEventListener('load', () => {
        // once loaded, add 'loaded' class to script element
        // to enable geolocationLibraryIsLoaded fn to be true
        const scriptElement = document.querySelector('script#google');
        if (scriptElement) {
          scriptElement.setAttribute('class', 'loaded');
        }
        scriptLoadingListeners.forEach(resolver => resolver());
        // once we've invoked all the resolvers clear out the listener arrays
        clearListenerArrays();
      });
      script.addEventListener('error', () => {
        // remove library script element from head
        // something failed so let's enable retry
        const scriptElement = document.querySelector('script#google');
        if (scriptElement?.parentElement) {
          scriptElement.parentElement.removeChild(scriptElement);
        }
        scriptLoadingListenersReject.forEach(rejecter => rejecter());
        // once we've invoked all the rejecters clear out the listener arrays
        clearListenerArrays();
      });
    }
  });
}

export function getUsersCurrentPosition(): Promise<ILocation> {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      position =>
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy
        }),
      positionError => reject(positionError),
      { timeout: GET_CURRENT_POSITION_TIMEOUT }
    );
  });
}

export function determinePermissionsFromGetCurrentPositionErrorCode(
  errorCode?: PermissionState
): GeolocationPermissionStates {
  switch (errorCode) {
    case PermissionState.TIMEOUT:
      return GeolocationPermissionStates.PROMPT;
    case PermissionState.DENIED:
    case PermissionState.UNAVAILABLE:
    default:
      return GeolocationPermissionStates.DENIED;
  }
}

/**
 * navigator.permissions has a status of 'Working Draft' on MDN:
 * https://developer.mozilla.org/en-US/docs/Web/API/Permissions/query
 *
 * It is still not available on many mobile browsers. This handles it's
 * fallback, but doesn't provide a way to find 'granted' permissions.
 */
export function permissionsFallback() {
  return getUsersCurrentPosition()
    .then(() => {
      return GeolocationPermissionStates.GRANTED;
    })
    .catch(positionError => {
      // TODO: add log noting that permissionsFallback failed
      if (positionError?.code) {
        return Promise.resolve(
          determinePermissionsFromGetCurrentPositionErrorCode(positionError.code)
        );
      }
      return Promise.resolve(GeolocationPermissionStates.DENIED);
    });
}

export async function checkIfLocationPermissionIsPreapproved(): Promise<
  GeolocationPermissionStates
> {
  try {
    // if we have navigator.permissions
    if (navigator.permissions) {
      // run query on permissions
      const { state } = await navigator.permissions.query({ name: 'geolocation' });
      switch (state) {
        case GeolocationPermissionStates.GRANTED:
          return GeolocationPermissionStates.GRANTED;
        case GeolocationPermissionStates.DENIED:
          return GeolocationPermissionStates.DENIED;
        // DEFAULT: something went wrong so let's re-prompt
        default:
          return GeolocationPermissionStates.PROMPT;
      }
    }
    // otherwise, throw error to trigger fallback
    else {
      throw new Error('No navigator permissions, try fallback');
    }
  } catch (e) {
    // run permissionsFallback to check for geolocation (see separate flow)
    return await permissionsFallback();
  }
}

export function getUsersGeocodeInformation(userLocation: ILocation) {
  return loadGeolocationLibrary().then(() => {
    const geocoder = new google.maps.Geocoder();

    return new Promise<google.maps.GeocoderResult>((resolve, reject) => {
      geocoder.geocode({ location: userLocation }, (results, status) => {
        if (status === google.maps.GeocoderStatus.OK) {
          const result = results[0]!;
          return resolve(result);
        }

        reject();
      });
    });
  });
}

const isNotNilFloat = (n?: number | null) => typeof n === 'number' && Number.isFinite(n);

export const isValidPosition = (
  position: {
    lat?: number | null;
    lng?: number | null;
  } | null
): position is ILocation => {
  return (
    !!position &&
    'lat' in position &&
    isNotNilFloat(position.lat) &&
    'lng' in position &&
    isNotNilFloat(position.lng)
  );
};

// stubs for native-only functionality
export const initializeRadar = noop as any;
export const setRadarUser = noop as any;
